// Which puck a peak on the map's POI layer draws: green when the signed-in walker has bagged it,
// slate when they have not. The web twin of the apps' PeakPuckIcon (Android helpers/PeakPuckIcon.kt,
// iOS Modules/MyPeaks/Models/PeakPuckIcon.swift), so a peak looks the same on every map HiiKER has.

export const PEAK_POI_TYPE = 'peak';
export const BAGGED_PEAK_PUCK = 'puck_icon_peak';
export const UNBAGGED_PEAK_PUCK = 'puck_icon_peak_unbagged';

export interface BaggedPeakRow {
  amenityId: string | number | null;
  deletedAt?: string | null;
}

/**
 * The amenity ids to draw green, as numbers.
 *
 * Numbers because the POI tileset writes `id` as a JSON number while GraphQL hands `amenityId` back
 * as an `ID` string — and an `in` over strings silently matches nothing. Tombstoned rows (an
 * un-bagged peak) are left out. Sorted and de-duplicated so the same set always builds the same
 * expression.
 */
export function baggedPeakIdNumbers(rows: BaggedPeakRow[] | null | undefined): number[] {
  const ids = new Set<number>();
  for (const row of rows ?? []) {
    if (!row || row.deletedAt) continue;
    const id = Number(row.amenityId);
    if (row.amenityId !== null && row.amenityId !== '' && Number.isFinite(id)) ids.add(id);
  }
  return Array.from(ids).sort((a, b) => a - b);
}

/**
 * The `icon-image` value for a POI feature whose type is `peak`.
 *
 * Only ever used inside the POI layer's `match` on `type`, so the `in` never sees an accommodation
 * — those share the amenity id space and a bare id check could colour a hotel. With nothing bagged
 * the `in` is dropped: it could only ever answer false.
 */
export function peakPuckImage(baggedIds: number[]): string | unknown[] {
  if (baggedIds.length === 0) return UNBAGGED_PEAK_PUCK;
  return ['case', ['in', ['get', 'id'], ['literal', baggedIds]], BAGGED_PEAK_PUCK, UNBAGGED_PEAK_PUCK];
}

// The two pucks, drawn on a canvas at the sprite's own size (170 px at pixelRatio 2), for a style
// whose sprite predates the unbagged puck. A symbol asked for an image the style lacks draws
// nothing, so without this every peak would vanish from such a map. Same shapes and colours as
// puckSvg in flyoverPeakOverlay.ts.
const PUCK_SIZE = 170;
const PUCK_PIXEL_RATIO = 2;
const MOUNTAIN_STROKE = '#95A629';
const DISC_COLOURS: Record<string, string> = {
  [BAGGED_PEAK_PUCK]: '#95A629',
  [UNBAGGED_PEAK_PUCK]: '#575E73',
};

function drawPuck(imageId: string) {
  const canvas = document.createElement('canvas');
  canvas.width = PUCK_SIZE;
  canvas.height = PUCK_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Drawn in the SVG's 85-unit viewBox, scaled up to the canvas.
  ctx.scale(PUCK_SIZE / 85, PUCK_SIZE / 85);

  ctx.beginPath();
  ctx.arc(42.6, 42.8, 41.94, 0, Math.PI * 2);
  ctx.fillStyle = DISC_COLOURS[imageId];
  ctx.fill();

  ctx.beginPath();
  ctx.arc(42.6, 42.8, 40.44, 0, Math.PI * 2);
  ctx.lineWidth = 3;
  ctx.strokeStyle = '#fff';
  ctx.stroke();

  const mountains = [
    [[51.001, 58.391], [16.65, 58.391], [33.826, 33.027]],
    [[68.576, 58.39], [26.037, 58.39], [47.207, 26.836]],
  ];
  for (const [first, ...rest] of mountains) {
    ctx.beginPath();
    ctx.moveTo(first[0], first[1]);
    rest.forEach(([x, y]) => ctx.lineTo(x, y));
    ctx.closePath();
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.strokeStyle = MOUNTAIN_STROKE;
    ctx.stroke();
  }

  const imageData = ctx.getImageData(0, 0, PUCK_SIZE, PUCK_SIZE);
  return { width: PUCK_SIZE, height: PUCK_SIZE, data: new Uint8Array(imageData.data.buffer) };
}

const mapsWithFallback = new WeakSet<object>();

/**
 * Registers either puck the moment the style asks for one it does not have.
 *
 * `styleimagemissing` rather than a `hasImage` check up front: the sprite can still be loading when
 * the layer is added, and adding our own image first would shadow the sprite's artwork. The listener
 * survives style swaps, so it is attached once per map.
 */
export function ensurePeakPuckFallback(map: any) {
  if (!map || mapsWithFallback.has(map)) return;
  mapsWithFallback.add(map);
  map.on('styleimagemissing', (e: { id: string }) => {
    if (!(e.id in DISC_COLOURS) || map.hasImage(e.id)) return;
    const image = drawPuck(e.id);
    if (image) map.addImage(e.id, image, { pixelRatio: PUCK_PIXEL_RATIO });
  });
}
